// Undo stack for coloring fills.
//
// Stores ImageData snapshots. Index 0 is always the initial loaded image
// (never dropped). Indices 1..N are post-fill states. When the cap is hit,
// the oldest *fill* snapshot is dropped — initial is kept so reset() always
// works, and the most recent fills stay undoable.
//
// Spec: "max 8 步" undo. We interpret that as up to 8 fills can be undone,
// i.e. the stack holds initial + up to 8 fill snapshots = 9 entries max.

export interface HistorySnapshot {
  data: Uint8ClampedArray
  width: number
  height: number
}

export class HistoryStack {
  private snapshots: HistorySnapshot[] = []
  /** Max number of *fill* snapshots (initial doesn't count). */
  private readonly maxFills: number

  constructor(maxFills = 8) {
    if (maxFills < 1) throw new Error('maxFills must be >= 1')
    this.maxFills = maxFills
  }

  /** Replace history with a fresh initial state. Called on loadImage. */
  reset(initial: HistorySnapshot): void {
    this.snapshots = [cloneSnapshot(initial)]
  }

  /** Push a post-fill snapshot. Drops oldest fill if cap exceeded. */
  pushFill(snap: HistorySnapshot): void {
    if (this.snapshots.length === 0) {
      throw new Error('history not initialized — call reset() first')
    }
    this.snapshots.push(cloneSnapshot(snap))
    // initial + maxFills fills = maxFills + 1 entries
    if (this.snapshots.length > this.maxFills + 1) {
      this.snapshots.splice(1, 1) // drop oldest fill, keep initial at [0]
    }
  }

  /** Pop the latest fill, return the snapshot to render (state before that fill). */
  undo(): HistorySnapshot | null {
    if (this.snapshots.length <= 1) return null
    this.snapshots.pop()
    const top = this.snapshots[this.snapshots.length - 1]
    return top ? cloneSnapshot(top) : null
  }

  canUndo(): boolean {
    return this.snapshots.length > 1
  }

  /** Number of undoable fills currently in the stack. */
  fillCount(): number {
    return Math.max(0, this.snapshots.length - 1)
  }

  /** Restore to the initial loaded image. */
  resetToInitial(): HistorySnapshot | null {
    const initial = this.snapshots[0]
    if (!initial) return null
    this.snapshots = [initial]
    return cloneSnapshot(initial)
  }
}

function cloneSnapshot(s: HistorySnapshot): HistorySnapshot {
  // Uint8ClampedArray.slice() copies; we need this so the engine's mutation
  // of the canvas's ImageData doesn't ripple into our stored history entry.
  return { data: new Uint8ClampedArray(s.data), width: s.width, height: s.height }
}
